import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Image from 'next/image'
import { Play, X } from 'lucide-react'

import { FOUNDER_VIDEO_ID } from '@/constants/landing'

const VIDEO_TITLE = "Introducing Skill Kafe: vision and mission by founder Sukesh Dash"

const FounderVideo: FC = (): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const [playing, setPlaying] = useState(false)

    const openVideo = (): void => {
        setPlaying(true)
        dialogRef.current?.showModal()
    }

    const closeVideo = (): void => {
        dialogRef.current?.close()
    }

    return (
        <div className='reveal'>
            <button
                type='button'
                onClick={openVideo}
                aria-haspopup='dialog'
                className='group relative block w-full overflow-hidden rounded-[2rem] bg-brand-navy shadow-2xl shadow-brand-navy/25 ring-1 ring-black/5 focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-primary'
            >
                <span className='relative block aspect-video'>
                    <Image
                        src='/images/founder-video.jpg'
                        alt=''
                        fill
                        sizes='(min-width: 1024px) 40rem, 100vw'
                        className='object-cover transition-transform duration-700 group-hover:scale-[1.03]'
                    />
                </span>

                <span aria-hidden='true' className='absolute inset-0 bg-brand-navy/10 transition-colors group-hover:bg-brand-navy/25' />

                <span aria-hidden='true' className='absolute inset-0 grid place-items-center'>
                    <span className='grid size-20 place-items-center rounded-full bg-white text-primary shadow-xl ring-8 ring-white/25 transition-transform duration-300 group-hover:scale-105 sm:size-24'>
                        <Play className='ml-1 size-8 sm:size-9' fill='currentColor' strokeWidth={0} />
                    </span>
                </span>

                <span className='sr-only'>Play video: {VIDEO_TITLE}</span>
            </button>

            <p className='mt-4 text-sm text-muted-foreground'>
                Watch: the vision and mission behind Skillkafe, by our founder Sukesh Dash.
            </p>

            <dialog
                ref={dialogRef}
                aria-label={VIDEO_TITLE}
                onClose={() => setPlaying(false)}
                onClick={event => {
                    if (event.target === dialogRef.current) closeVideo()
                }}
                className='m-auto w-[min(92vw,64rem)] max-w-none overflow-visible bg-transparent p-0 backdrop:bg-brand-navy/85 backdrop:backdrop-blur-sm'
            >
                <div className='relative pt-14'>
                    <button
                        type='button'
                        onClick={closeVideo}
                        className='absolute right-0 top-0 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25'
                    >
                        <X className='size-4' aria-hidden='true' />
                        Close video
                    </button>

                    <div className='aspect-video overflow-hidden rounded-2xl bg-black shadow-2xl'>
                        {playing && (
                            <iframe
                                src={`https://www.youtube-nocookie.com/embed/${FOUNDER_VIDEO_ID}?autoplay=1&rel=0`}
                                title={VIDEO_TITLE}
                                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                                allowFullScreen
                                referrerPolicy='strict-origin-when-cross-origin'
                                className='size-full'
                            />
                        )}
                    </div>
                </div>
            </dialog>
        </div>
    )
}

export default FounderVideo
